import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "wishlistItems";

const catalogById = new Map(
  productData.map((product) => [String(product.id), product])
);

const getId = (productOrId) => {
  const rawId =
    productOrId && typeof productOrId === "object"
      ? productOrId.id ?? productOrId.productId
      : productOrId;

  if (rawId === null || rawId === undefined || rawId === "") return null;

  if (typeof rawId === "string" && /^\d+$/.test(rawId.trim())) {
    return Number(rawId);
  }

  return rawId;
};

const getCatalogProduct = (productOrId) => {
  const id = getId(productOrId);
  return id === null ? null : catalogById.get(String(id)) ?? null;
};

const toPositiveQuantity = (value, fallback = 1) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0
    ? Math.max(1, Math.floor(parsed))
    : fallback;
};

const normalizeVariant = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, option]) => option !== undefined && option !== null && option !== "")
      .sort(([left], [right]) => left.localeCompare(right))
  );
};

const variantFromLegacyArgs = (variantOrSize, color) => {
  if (variantOrSize && typeof variantOrSize === "object") {
    return normalizeVariant(
      variantOrSize.variant ?? variantOrSize.variants ?? variantOrSize
    );
  }

  return normalizeVariant({ size: variantOrSize, color });
};

const createLineKey = (id, variant = {}) =>
  `${String(id)}::${JSON.stringify(normalizeVariant(variant))}`;

const productSnapshot = (productOrId) => {
  const id = getId(productOrId);
  if (id === null) return null;

  const supplied =
    productOrId && typeof productOrId === "object" ? productOrId : {};
  const catalogProduct = getCatalogProduct(id) ?? {};
  const product = { ...catalogProduct, ...supplied };
  const numericPrice = Number(product.price);
  const images = Array.isArray(product.images)
    ? product.images.filter(Boolean)
    : product.image
    ? [product.image]
    : [];

  return {
    id,
    productId: id,
    ...(product.slug ? { slug: product.slug } : {}),
    ...(product.name ? { name: product.name } : {}),
    ...(product.category ? { category: product.category } : {}),
    ...(Number.isFinite(numericPrice) ? { price: numericPrice } : {}),
    ...(images.length ? { images, image: images[0] } : {}),
  };
};

const normalizeCartItem = (item) => {
  if (!item || typeof item !== "object") return null;

  const snapshot = productSnapshot(item);
  if (!snapshot) return null;

  const storedVariant = normalizeVariant(item.variant ?? item.variants);
  const variant = Object.keys(storedVariant).length
    ? storedVariant
    : normalizeVariant({ size: item.size, color: item.color });

  return {
    ...snapshot,
    quantity: toPositiveQuantity(item.quantity),
    variant,
    ...(variant.size ? { size: variant.size } : {}),
    ...(variant.color ? { color: variant.color } : {}),
    lineKey: createLineKey(snapshot.id, variant),
  };
};

const normalizeCart = (value) => {
  if (!Array.isArray(value)) return [];

  const mergedItems = new Map();

  value.forEach((rawItem) => {
    const item = normalizeCartItem(rawItem);
    if (!item) return;

    const existing = mergedItems.get(item.lineKey);
    mergedItems.set(
      item.lineKey,
      existing
        ? { ...existing, quantity: existing.quantity + item.quantity }
        : item
    );
  });

  return Array.from(mergedItems.values());
};

const normalizeWishlistItem = (item) => productSnapshot(item);

const normalizeWishlist = (value) => {
  if (!Array.isArray(value)) return [];

  const uniqueItems = new Map();
  value.forEach((rawItem) => {
    const item = normalizeWishlistItem(rawItem);
    if (item) uniqueItems.set(String(item.id), item);
  });

  return Array.from(uniqueItems.values());
};

const readStoredArray = (key) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return [];
    const savedValue = window.localStorage.getItem(key);
    return savedValue ? JSON.parse(savedValue) : [];
  } catch {
    return [];
  }
};

const writeStoredValue = (key, value) => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Storage can be unavailable in private browsing or restricted environments.
  }
};

const emptyFunction = () => undefined;

const defaultContextValue = {
  cartItems: [],
  wishlistItems: [],
  wishlist: [],
  addToCart: emptyFunction,
  addCart: emptyFunction,
  removeFromCart: emptyFunction,
  removeCart: emptyFunction,
  updateQuantity: emptyFunction,
  clearCart: emptyFunction,
  addToWishlist: emptyFunction,
  addWishlist: emptyFunction,
  removeFromWishlist: emptyFunction,
  removeWishlist: emptyFunction,
  toggleWishlist: emptyFunction,
  isInWishlist: () => false,
  isInCart: () => false,
  cartCount: 0,
  cartItemCount: 0,
  uniqueCartCount: 0,
  cartSubtotal: 0,
  subtotal: 0,
  wishlistCount: 0,
};

export const CartContext = createContext(defaultContextValue);

const resolveAddInput = (
  productOrId,
  quantityOrOptions = 1,
  variantOrSize,
  legacyColor
) => {
  let quantity = quantityOrOptions;
  let variant = variantFromLegacyArgs(variantOrSize, legacyColor);

  if (
    quantityOrOptions &&
    typeof quantityOrOptions === "object" &&
    !Array.isArray(quantityOrOptions)
  ) {
    quantity = quantityOrOptions.quantity ?? 1;
    variant = variantFromLegacyArgs(
      quantityOrOptions.variant ??
        quantityOrOptions.variants ??
        {
          size: quantityOrOptions.size,
          color: quantityOrOptions.color,
        }
    );
  }

  if (!Object.keys(variant).length && productOrId && typeof productOrId === "object") {
    variant = variantFromLegacyArgs(
      productOrId.selectedVariant ?? productOrId.variant
    );
  }

  const snapshot = productSnapshot(productOrId);
  if (!snapshot) return null;

  const product =
    (productOrId && typeof productOrId === "object" && productOrId) ||
    getCatalogProduct(snapshot.id);
  const requestedQuantity = toPositiveQuantity(quantity);
  const stock = Number(product?.stock);
  const availableQuantity = Number.isFinite(stock)
    ? Math.min(requestedQuantity, Math.max(0, Math.floor(stock)))
    : requestedQuantity;

  if (availableQuantity < 1) return null;

  return {
    ...snapshot,
    quantity: availableQuantity,
    variant,
    ...(variant.size ? { size: variant.size } : {}),
    ...(variant.color ? { color: variant.color } : {}),
    lineKey: createLineKey(snapshot.id, variant),
    stock: Number.isFinite(stock) ? Math.max(0, Math.floor(stock)) : null,
  };
};

const resolveTarget = (productOrLineKey, variantOrSize, color) => {
  const explicitLineKey =
    productOrLineKey && typeof productOrLineKey === "object"
      ? productOrLineKey.lineKey
      : typeof productOrLineKey === "string" && productOrLineKey.includes("::")
      ? productOrLineKey
      : null;

  if (explicitLineKey) return { lineKey: explicitLineKey, id: null, hasVariant: true };

  const id = getId(productOrLineKey);
  const objectVariant =
    productOrLineKey && typeof productOrLineKey === "object"
      ? productOrLineKey.variant ?? productOrLineKey.variants
      : null;
  const hasVariant =
    objectVariant !== null || variantOrSize !== undefined || color !== undefined;
  const variant = variantFromLegacyArgs(objectVariant ?? variantOrSize, color);

  return {
    id,
    hasVariant,
    lineKey: id !== null && hasVariant ? createLineKey(id, variant) : null,
  };
};

const targetMatches = (item, target) => {
  if (target.lineKey) return item.lineKey === target.lineKey;
  return target.id !== null && String(item.id) === String(target.id);
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    normalizeCart(readStoredArray(CART_STORAGE_KEY))
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    normalizeWishlist(readStoredArray(WISHLIST_STORAGE_KEY))
  );

  useEffect(() => {
    writeStoredValue(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    writeStoredValue(WISHLIST_STORAGE_KEY, wishlistItems);
  }, [wishlistItems]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncFromStorage = (event) => {
      if (event.key === CART_STORAGE_KEY) {
        try {
          setCartItems(normalizeCart(event.newValue ? JSON.parse(event.newValue) : []));
        } catch {
          setCartItems([]);
        }
      }

      if (event.key === WISHLIST_STORAGE_KEY) {
        try {
          setWishlistItems(
            normalizeWishlist(event.newValue ? JSON.parse(event.newValue) : [])
          );
        } catch {
          setWishlistItems([]);
        }
      }
    };

    window.addEventListener("storage", syncFromStorage);
    return () => window.removeEventListener("storage", syncFromStorage);
  }, []);

  const addToCart = useCallback(
    (productOrId, quantityOrOptions = 1, variantOrSize, legacyColor) => {
      const incomingItem = resolveAddInput(
        productOrId,
        quantityOrOptions,
        variantOrSize,
        legacyColor
      );
      if (!incomingItem) return;

      setCartItems((currentItems) => {
        const existingItem = currentItems.find(
          (item) => item.lineKey === incomingItem.lineKey
        );

        if (!existingItem) return [...currentItems, incomingItem];

        const combinedQuantity = existingItem.quantity + incomingItem.quantity;
        const quantity = Number.isFinite(incomingItem.stock)
          ? Math.min(combinedQuantity, incomingItem.stock)
          : combinedQuantity;

        return currentItems.map((item) =>
          item.lineKey === incomingItem.lineKey
            ? { ...item, ...incomingItem, quantity }
            : item
        );
      });
    },
    []
  );

  const removeFromCart = useCallback(
    (productOrLineKey, variantOrSize, legacyColor) => {
      const target = resolveTarget(productOrLineKey, variantOrSize, legacyColor);
      setCartItems((currentItems) =>
        currentItems.filter((item) => !targetMatches(item, target))
      );
    },
    []
  );

  const updateQuantity = useCallback(
    (productOrLineKey, secondArgument, thirdArgument, fourthArgument) => {
      let quantity;
      let target;

      if (fourthArgument !== undefined) {
        // Legacy signature: updateQuantity(id, size, color, quantity)
        quantity = fourthArgument;
        target = resolveTarget(productOrLineKey, secondArgument, thirdArgument);
      } else if (
        secondArgument &&
        typeof secondArgument === "object" &&
        thirdArgument !== undefined
      ) {
        // Variant-first signature: updateQuantity(id, variant, quantity)
        quantity = thirdArgument;
        target = resolveTarget(productOrLineKey, secondArgument);
      } else {
        // Modern signature: updateQuantity(lineKeyOrId, quantity, variant?)
        quantity = secondArgument;
        target = resolveTarget(productOrLineKey, thirdArgument);
      }

      const parsedQuantity = Number(quantity);
      if (!Number.isFinite(parsedQuantity)) return;

      if (parsedQuantity <= 0) {
        setCartItems((currentItems) =>
          currentItems.filter((item) => !targetMatches(item, target))
        );
        return;
      }

      const normalizedQuantity = Math.max(1, Math.floor(parsedQuantity));
      setCartItems((currentItems) =>
        currentItems.map((item) => {
          if (!targetMatches(item, target)) return item;
          const quantityForItem = Number.isFinite(item.stock)
            ? Math.min(normalizedQuantity, item.stock)
            : normalizedQuantity;
          return { ...item, quantity: quantityForItem };
        })
      );
    },
    []
  );

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = useCallback((productOrId) => {
    const item = normalizeWishlistItem(productOrId);
    if (!item) return;

    setWishlistItems((currentItems) =>
      currentItems.some((currentItem) => String(currentItem.id) === String(item.id))
        ? currentItems
        : [...currentItems, item]
    );
  }, []);

  const removeFromWishlist = useCallback((productOrId) => {
    const id = getId(productOrId);
    if (id === null) return;
    setWishlistItems((currentItems) =>
      currentItems.filter((item) => String(item.id) !== String(id))
    );
  }, []);

  const toggleWishlist = useCallback((productOrId) => {
    const item = normalizeWishlistItem(productOrId);
    if (!item) return;

    setWishlistItems((currentItems) => {
      const exists = currentItems.some(
        (currentItem) => String(currentItem.id) === String(item.id)
      );
      return exists
        ? currentItems.filter(
            (currentItem) => String(currentItem.id) !== String(item.id)
          )
        : [...currentItems, item];
    });
  }, []);

  const isInWishlist = useCallback(
    (productOrId) => {
      const id = getId(productOrId);
      return (
        id !== null &&
        wishlistItems.some((item) => String(item.id) === String(id))
      );
    },
    [wishlistItems]
  );

  const isInCart = useCallback(
    (productOrId) => {
      const id = getId(productOrId);
      return (
        id !== null && cartItems.some((item) => String(item.id) === String(id))
      );
    },
    [cartItems]
  );

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const cartSubtotal = useMemo(
    () =>
      cartItems.reduce((total, item) => {
        const catalogPrice = Number(getCatalogProduct(item.id)?.price);
        const storedPrice = Number(item.price);
        const price = Number.isFinite(storedPrice)
          ? storedPrice
          : Number.isFinite(catalogPrice)
          ? catalogPrice
          : 0;
        return total + price * item.quantity;
      }, 0),
    [cartItems]
  );

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlistItems,
      wishlist: wishlistItems,
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      addWishlist: addToWishlist,
      removeFromWishlist,
      removeWishlist: removeFromWishlist,
      toggleWishlist,
      isInWishlist,
      isInCart,
      cartCount,
      cartItemCount: cartCount,
      uniqueCartCount: cartItems.length,
      cartSubtotal,
      subtotal: cartSubtotal,
      wishlistCount: wishlistItems.length,
    }),
    [
      addToCart,
      addToWishlist,
      cartCount,
      cartItems,
      cartSubtotal,
      clearCart,
      isInCart,
      isInWishlist,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlistItems,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
